import * as THREE from 'three';

const textureCache = new Map();
const loadingPromises = new Map();
const loader = new THREE.TextureLoader();

export function getTexture(url) {
  return textureCache.get(url) || null;
}

export function loadTexture(url) {
  if (!url) return Promise.resolve(null);

  if (textureCache.has(url)) {
    return Promise.resolve(textureCache.get(url));
  }

  if (loadingPromises.has(url)) {
    return loadingPromises.get(url);
  }

  const promise = new Promise((resolve) => {
    loader.load(
      url,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        textureCache.set(url, texture);
        loadingPromises.delete(url);
        resolve(texture);
      },
      undefined,
      () => {
        loadingPromises.delete(url);
        resolve(null);
      }
    );
  });

  loadingPromises.set(url, promise);
  return promise;
}

export function preloadTextures(urls, { batchSize = 10, onProgress } = {}) {
  const validUrls = urls.filter(Boolean);
  const uncachedUrls = validUrls.filter((url) => !textureCache.has(url));

  if (uncachedUrls.length === 0) {
    onProgress?.(1);
    return Promise.resolve();
  }

  let loaded = validUrls.length - uncachedUrls.length;
  const total = validUrls.length;

  const batches = [];
  for (let i = 0; i < uncachedUrls.length; i += batchSize) {
    batches.push(uncachedUrls.slice(i, i + batchSize));
  }

  return batches.reduce((chain, batch) => {
    return chain.then(() => {
      return Promise.all(
        batch.map((url) =>
          loadTexture(url).then(() => {
            loaded++;
            onProgress?.(loaded / total);
          })
        )
      );
    });
  }, Promise.resolve());
}

export function getCacheStats() {
  return {
    cached: textureCache.size,
    loading: loadingPromises.size,
  };
}

export function clearCache() {
  textureCache.forEach((texture) => {
    texture.dispose();
  });
  textureCache.clear();
  loadingPromises.clear();
}
